// src/app/routes.tsx
// ════════════════════════════════════════════════════════════
//  Route-level code splitting via React.lazy + Suspense.
//  Each page is loaded on demand — reduces initial bundle size.
//  LoaderUi is shown while the chunk is being fetched.
// ════════════════════════════════════════════════════════════

import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import LoaderUi from "../components/ui/Loader/LoaderUi";

// ── Lazy page imports ─────────────────────────────────────────
const HomeWrapper = lazy(() => import("../modules/home/HomeWrapper"));
const AboutWrapper = lazy(() => import("../modules/about/AboutWrapper"));
const ServicesWrapper = lazy(
  () => import("../modules/services/ServicesWrapper"),
);
const ProductsWrapper = lazy(
  () => import("../modules/products/ProductsWrapper"),
);
const ClienteleWrapper = lazy(
  () => import("../modules/clientele/ClienteleWrapper"),
);
const ContactWrapper = lazy(() => import("../modules/contact/ContactWrapper"));
const ServiceDetailWrapper = lazy(
  () => import("../modules/servicedetail/ServiceDetailWrapper"),
);
const BlogListWrapper = lazy(() => import("../modules/blog/BlogListWrapper"));
const BlogDetailWrapper = lazy(
  () => import("../modules/blog/BlogDetailWrapper"),
);
const Error404Ui = lazy(() =>
  import("../modules/errors/ErrorPages").then((m) => ({
    default: m.Error404Ui,
  })),
);
const Error500Ui = lazy(() =>
  import("../modules/errors/ErrorPages").then((m) => ({
    default: m.Error500Ui,
  })),
);
const NotFoundUi = lazy(() => import("../modules/notfound/NotFoundUi"));

const AppRoutes: React.FC = () => (
  <Suspense fallback={<LoaderUi />}>
    <Routes>
      {/* Main pages */}
      <Route path="/" element={<HomeWrapper />} />
      <Route path="/about" element={<AboutWrapper />} />
      <Route path="/services" element={<ServicesWrapper />} />
      <Route path="/products" element={<ProductsWrapper />} />
      <Route path="/clientele" element={<ClienteleWrapper />} />
      <Route path="/contact" element={<ContactWrapper />} />

      {/* Blog routes */}
      <Route path="/blog" element={<BlogListWrapper />} />
      <Route path="/blog/:slug" element={<BlogDetailWrapper />} />

      {/* Error pages */}
      <Route path="/error-500" element={<Error500Ui />} />
      <Route path="/error-404" element={<Error404Ui />} />

      {/* Sub-service routes — MUST be before generic /services/:slug */}
      <Route
        path="/services/software-development/:slug"
        element={<ServiceDetailWrapper type="sw-sub" />}
      />
      <Route
        path="/services/embedded-systems/:slug"
        element={<ServiceDetailWrapper type="embedded" />}
      />
      <Route
        path="/services/digital-marketing/:slug"
        element={<ServiceDetailWrapper type="dm-sub" />}
      />
      <Route
        path="/services/enterprise-retail-solutions/:slug"
        element={<ServiceDetailWrapper type="retail-sub" />}
      />
      <Route
        path="/services/enterprise-restaurant-product/:slug"
        element={<ServiceDetailWrapper type="restaurant-sub" />}
      />

      {/* Generic service detail */}
      <Route
        path="/services/:slug"
        element={<ServiceDetailWrapper type="main" />}
      />

      {/* ── Redirects for common alternate URLs ──────────────── */}
      <Route path="/aboutus" element={<Navigate to="/about" replace />} />
      <Route path="/about-us" element={<Navigate to="/about" replace />} />
      <Route path="/contactus" element={<Navigate to="/contact" replace />} />
      <Route path="/contact-us" element={<Navigate to="/contact" replace />} />
      <Route path="/service" element={<Navigate to="/services" replace />} />
      <Route path="/product" element={<Navigate to="/products" replace />} />
      <Route path="/clients" element={<Navigate to="/clientele" replace />} />
      <Route path="/blogs" element={<Navigate to="/blog" replace />} />

      {/* 404 */}
      <Route path="*" element={<NotFoundUi />} />
    </Routes>
  </Suspense>
);

export default AppRoutes;
